import React, { useState, useEffect, useContext, useRef, useMemo } from 'react'
import ProductCard from '../../components/ProductCard'
import SearchCount from '../../components/SearchCount'
import Context from '../../context'
import Head from 'next/head'
import { InferGetServerSidePropsType } from 'next'
import { useRouter } from 'next/router'
import getConfig from 'next/config'
import CardSubCategories from '../../components/CardSubCategories'
import useDidMountEffect from '../../utils/useDidMountEffect'
import BackToTop from '../../components/BackToTop'
import Widget from '../../components/Widget'
import { useAuthAPI } from '../../hooks/api.hooks'

import { Swiper, SwiperSlide } from 'swiper/react'
import { Keyboard, Pagination, FreeMode, Navigation } from 'swiper/modules'

import 'swiper/css'
import 'swiper/css/free-mode'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/scrollbar'
import 'swiper/css/keyboard'

import { useIonModal } from '@ionic/react'
import Filters from '../../components/Filters'
import { enterAnimation, leaveAnimation } from '../../utils/modal-animations'
import SVG from '../../components/SVG'
import { formatF1String, setPageViewData, setSearchResultsData } from '../../utils/analytics'

const { publicRuntimeConfig } = getConfig()

interface StringMap { [key: string]: [string] }

const Products = ({
  items,
  searchTerm,
  unalteredTerm,
  widget,
  categoryMap,
  searchCategory,
  numberOfResults,
  fullCategoryList
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const { state, dispatch } = useContext(Context)
  const [itemsArr, setItemsArr] = useState(items)
  const [offset, setOffset] = useState(0)
  const [filters, setFilters] = useState({})
  const [totalRows, setTotalRows] = useState(numberOfResults)
  const [animated, setAnimated] = useState(false)
  const [categories, setCategories] = useState(categoryMap)
  const [selectedProductsLoaded, setSelectedProductsLoaded] = useState(true)
  const [selectedFilters, setSelectedFilters] = useState({
    facets: searchCategory ? { category: [searchCategory] } : {} as StringMap,
    sort: {}
  })
  const [grid, setOpen] = React.useState(false)
  const [showFilterLoader, setFilterLoader] = useState(false)
  const isFirstRun = useRef(true)
  const router = useRouter()
  const authAPICall = useAuthAPI()
  const filtersSet: boolean = useMemo(() => {
    const nonCategoryFilters = Object.keys(selectedFilters.facets).filter((item) => item !== 'category')
    return nonCategoryFilters.length > 0
  }, [selectedFilters])
  const isPageViewEventPushed = useRef(false)

  const removeFilterKeys = (facets) => {
    delete facets.avg_rating
    delete facets.rating_count
    return facets
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.br_data = window.br_data || {}
      window.br_data.test_data = publicRuntimeConfig.BR_TEST_DATA
      window.br_data.acct_id = publicRuntimeConfig.BR_ID
      window.br_data.ptype = 'search'
      window.br_data.search_term = searchTerm
      window.br_data.view_id = state.cart.store?.storenumber || publicRuntimeConfig.DEFAULT_STORE
      window.br_data.user_id = state.user.UID || state.deviceId;
      (() => {
        const brtrk: any = document.createElement('script')
        brtrk.type = 'text/javascript'
        brtrk.async = true
        brtrk.src = `//cdn.brcdn.com/v1/br-trk-${publicRuntimeConfig.BR_ID
          }.js?v=${Date.now()}`
        const s = document.getElementsByTagName('script')[0]
        s.parentNode.insertBefore(brtrk, s)
      })()
    }
  }, [])

  useEffect(() => {
    setOffset(0)
    setFilters({})
    setTotalRows(numberOfResults)
    const filters = isFirstRun.current && searchCategory ? selectedFilters.facets : {}
    if (isFirstRun.current) {
      isFirstRun.current = false
    } else {
      setSelectedFilters({
        facets: {},
        sort: {}
      })
    }

    setSelectedProductsLoaded(false)

    authAPICall(
      'search',
      'search',
      'GET',
      {
        store: state.cart.store?.storenumber || publicRuntimeConfig.DEFAULT_STORE,
        title: searchTerm,
        popkey: publicRuntimeConfig.POP_KEY,
        filters: Object.keys(filters).length && JSON.stringify(filters)
      },
      {}
    ).then((data) => {
      const brResponse = data.response
      setItemsArr([...brResponse])
      const newFacets = removeFilterKeys({ ...data.facets })
      setFilters(newFacets)
      setCategories(data.category_facets)
      setTotalRows(data.numFound)
      setSelectedProductsLoaded(true)
    })
  }, [state.cart.store?.storenumber])

  useEffect(() => {
    if (!isPageViewEventPushed.current) {
      const f1SearchTerm = formatF1String(searchTerm)
      setPageViewData(`Search|${f1SearchTerm}`, 'Search', `Search|${f1SearchTerm}`)
      setSearchResultsData(searchTerm, totalRows)
      isPageViewEventPushed.current = true
    } else {
      isPageViewEventPushed.current = false
    }
  }, [router.pathname])

  useDidMountEffect(() => {
    if (offset !== 0) {
      const filters = JSON.stringify(selectedFilters.facets)
      const sort = JSON.stringify(selectedFilters.sort)

      authAPICall(
        'search',
        'search',
        'GET',
        {
          store: state.cart.store?.storenumber || publicRuntimeConfig.DEFAULT_STORE,
          title: searchTerm,
          popkey: publicRuntimeConfig.POP_KEY,
          offset,
          filters,
          sort
        },
        {}
      ).then((data) => {
        setAnimated(false)
        setItemsArr([...itemsArr, ...data.response])
        setTotalRows(data.numFound)
      })
    }
  }, [offset])

  useDidMountEffect(() => {
    setOffset(0)
    setFilterLoader(true)
    const jsonFilters = JSON.stringify(selectedFilters.facets)
    const jsonSort = JSON.stringify(selectedFilters.sort)
    authAPICall(
      'search',
      'search',
      'GET',
      {
        store: state.cart.store?.storenumber || publicRuntimeConfig.DEFAULT_STORE,
        title: searchTerm,
        popkey: publicRuntimeConfig.POP_KEY,
        filters: jsonFilters,
        sort: jsonSort
      },
      {}
    ).then((data) => {
      setItemsArr([...data.response])
      const newFacets = removeFilterKeys({ ...data.facets })
      setFilters(newFacets)
      !selectedFilters.facets.category && setCategories(data.category_facets)
      setTotalRows(data.numFound)
      setFilterLoader(false)
    })
  }, [selectedFilters])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storedItems = state.recentlySearched
      const index = storedItems.findIndex(
        (e) => e.cleanedSearch === unalteredTerm.join(' ')
      )
      if (index !== -1) {
        const del = storedItems.splice(index, 1)
        storedItems.unshift(del[0])
      } else {
        storedItems.unshift({
          cleanedSearch: unalteredTerm.join(' ')
        })
        if (storedItems.length > 5) {
          storedItems.length = 5
        }
      }

      dispatch({ type: 'SET_RECENTLY_SEARCHED', payload: storedItems })
    }
  }, [])

  const renderContent = () => {
    if (itemsArr.length) {
      return (
        <div className={`product-list-page ${grid ? 'product-list-page__grid' : ''}`}>
          {itemsArr.map((item) => <ProductCard data={item} key={item.upc} vertical={!grid} />)}
        </div>
      )
    } else {
      return (
        !totalRows && selectedProductsLoaded && (
          <div className='search-page__empty'>
            <div className="empty-search"><SVG name="alert" label="alert" size="xl" /></div>
            <p className='search-page__empty-search-text'>
              No products match your search. <br></br>
              Please try again.
            </p>
          </div>
        )
      )
    }
  }

  const renderCategories = () => {
    const swiperConfig = {
      modules: [FreeMode, Pagination, Navigation, Keyboard],
      freeMode: true,
      pagination: { clickable: true },
      navigation: {
        nextEl: '.swiper-button-next-search',
        prevEl: '.swiper-button-prev-search'
      },
      spaceBetween: 16,
      keyboard: true,
      watchOverflow: true
    }

    const arr = categories.map((cat, i) => (
      <SwiperSlide key={i}>
        <button
          key={cat.cat_id}
          id={cat.cat_id}
          onClick={(e) => {
            setSelectedFilters({
              facets: { ...selectedFilters.facets, category: [cat.cat_id] },
              sort: { ...selectedFilters.sort }
            })
          }}
        >
          <CardSubCategories
            title={cat.cat_name}
            image={cat.image}
            campaignName={cat.campaign_name}
            dateReference={cat.date_reference} />
        </button>
      </SwiperSlide>
    ))
    return (
      <section className='swiper__section content-carousel' style={selectedFilters.facets.category && { display: 'none' }}>
        <div className="swiper-button-prev-search swiper-navbutton swiper-navbutton-prev"></div>
        <div className="swiper-button-next-search swiper-navbutton swiper-navbutton-next"></div>
        <Swiper {...swiperConfig} slidesPerView={'auto'}>
          {arr}
        </Swiper>
      </section>
    )
  }

  const renderChosenCategory = () => {
    return (
      <div className="search__selected-category-wrapper">
        <div
          className="search__selected-category"
          onClick={() => {
            const filterCopy = selectedFilters.facets
            delete filterCopy.category
            setSelectedFilters({
              facets: { ...filterCopy },
              ...selectedFilters
            })
          }}
        >
          <SVG name="close--white" label="close--white" size="xs" />
          <p className="search__selected-category-text">
            {fullCategoryList[selectedFilters.facets.category[0]]}
          </p>
        </div>
      </div>
    )
  }

  const submitForm = (filters, sort) => {
    setSelectedFilters({
      facets: { ...filters },
      sort: { ...sort }
    })
    window.br_data = window.br_data || {}
    window.br_data = window.br_data || {}
    window.br_data.acct_id = publicRuntimeConfig.BR_ID
    window.br_data.test_data = publicRuntimeConfig.BR_TEST_DATA
    window.br_data.ptype = 'category'
    window.br_data.view_id = state.store?.storenumber || publicRuntimeConfig.DEFAULT_STORE
    window.br_data.user_id = state.user.UID || state.deviceId

    window.br_data.orig_ref_url = location.href

    try {
      window.BrTrk.getTracker().updateBrData(window.br_data)
      window.BrTrk.getTracker().logPageView()
    } catch (e) {
      console.log(e)
    }
  }

  const handleDismiss = () => dismiss()
  const [present, dismiss] = useIonModal(Filters, { handleDismiss, data: filters, incomingData: { ...selectedFilters.facets }, incomingSort: selectedFilters.sort, submitForm, amt: itemsArr.length ? totalRows : 0, loading: showFilterLoader }
  )
  const toggleFilters = async () => {
    present({ cssClass: 'modal--filters', enterAnimation, leaveAnimation })
  }

  return (
    <div id="main-content" className="search-page">
      <Head>
        <title>Search for {unalteredTerm} at pOpshelf</title>
        <meta
          name="description"
          content="We believe shopping should be an experience you enjoy. A time to wander. Explore. Linger. A momentary escape just for you."
        />
        <meta name="robots" content="noindex" />
      </Head>
      <div className="search-page__container">
        <div className="search-page__header">

          <div className="search-page__count-mobile">
            <SearchCount searchTerm={searchTerm} results={totalRows} />
          </div>
          {!!categories.length &&
            <div className="carousel__parent carousel__parent-plp">
              {renderCategories()}
            </div>
          }
          {selectedFilters.facets.category && renderChosenCategory()}

          <div className="category-page__selectors">
            <div className="search-page__count-desktop">
              <SearchCount searchTerm={searchTerm} results={totalRows} />
            </div>
            <div className="category-page__selectors-container">
              <div className="category-page__selectors-icons">
                <button className={grid ? 'activate' : ''} onClick={() => setOpen(!grid)}>
                  <SVG name='plp-grid' label='grid view' size='md' />
                </button>
                <button className={!grid ? 'activate' : ''} onClick={() => setOpen(!grid)}>
                  <SVG name='plp-list' label='list view' size='md' />
                </button>
              </div>
              <button className={`filters__button ${filtersSet ? 'filters__button-bg' : ''}`} onClick={toggleFilters}>
                <p>{filtersSet ? 'Filtered' : 'Filters'}</p>
              </button>
            </div>
          </div>
        </div>
        <div>
          {renderContent()}
        </div>
        <div className="search-page__view-more">
          {(((totalRows > (offset + 24))) || animated) && (
            <button
              onClick={(e) => {
                if (!animated) {
                  setAnimated(true)
                  setOffset(offset + 24)
                }
              }}
              className={`btn__secondary btn__secondary--full${animated ? ' btn__animated' : ''
                }`}
            >
              VIEW MORE
            </button>
          )}
        </div>
        <div>
          <Widget type="p" slug={itemsArr[0]?.slug} data={widget} />
        </div>
        <BackToTop />
      </div>
    </div>
  )
}

export default Products

export const getServerSideProps = async (context) => {
  const { publicRuntimeConfig } = getConfig()

  const term = String(context.query.searchTerm).replace(/&/g, '')
  const searchTitle = encodeURI(term)

  const res = await fetch(
    `${publicRuntimeConfig.APP_ENDPOINT}/api/search/${publicRuntimeConfig.ENV}/search?store=${publicRuntimeConfig.DEFAULT_STORE}&title=${searchTitle}&popkey=${publicRuntimeConfig.POP_KEY}`,
    {
      headers: {
        deviceid: ''
      }
    }
  )

  const items = await res.json()

  const widgetDataRes = await fetch(
    `${publicRuntimeConfig.APP_ENDPOINT}/api/productinfo/${publicRuntimeConfig.ENV}/recommended?slug=${items?.response[0]?.slug}&type=p&store=${publicRuntimeConfig.DEFAULT_STORE}&popkey=${publicRuntimeConfig.POP_KEY}`,
    {
      headers: {
        deviceid: ''
      }
    }
  )
  const widgetData = await widgetDataRes.json()

  return {
    props: {
      items: items.response,
      searchTerm: term,
      unalteredTerm: context.query.searchTerm,
      categoryMap: items.category_facets,
      fullCategoryList: items.category_map,
      searchCategory: context.query.category || '',
      numberOfResults: items.numFound,
      widget: widgetData.body.data
    }
  }
}
